import { useState } from 'react';
import {
  ExtraordinaryAbilityEvidence,
  EvidenceCategoryKey,
  EVIDENCE_CATEGORY_LABELS,
  EVIDENCE_CATEGORY_ICONS,
  EvidenceItem,
} from '../types';
import styles from '../form-submissions.module.css';
import { ChevronDown, ExternalLink } from 'lucide-react';

interface EvidenceSectionProps {
  evidence: ExtraordinaryAbilityEvidence;
}

export function EvidenceSection({ evidence }: EvidenceSectionProps) {
  const [openCategories, setOpenCategories] = useState<Set<EvidenceCategoryKey>>(new Set());

  const toggleCategory = (key: EvidenceCategoryKey) => {
    const newOpen = new Set(openCategories);
    if (newOpen.has(key)) {
      newOpen.delete(key);
    } else {
      newOpen.add(key);
    }
    setOpenCategories(newOpen);
  };

  // Count criteria met
  const criteriaMet = (Object.keys(evidence) as EvidenceCategoryKey[]).filter(
    key => evidence[key].length > 0
  ).length;

  return (
    <div className={styles.evidenceSection}>
      {/* Progress Header */}
      <div className={styles.progressWrapper}>
        <div className={styles.progressHeader}>
          <span className={styles.progressLabel}>Extraordinary Ability Evidence</span>
          <span className={styles.progressValue}>{criteriaMet} of 8 criteria</span>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${(criteriaMet / 8) * 100}%` }} />
        </div>
      </div>

      {/* Evidence Categories */}
      {(Object.keys(evidence) as EvidenceCategoryKey[]).map(key => {
        const items = evidence[key];
        const isOpen = openCategories.has(key);
        const hasItems = items.length > 0;

        return (
          <div key={key}>
            <div
              className={styles.evidenceHeader}
              onClick={() => hasItems && toggleCategory(key)}
              style={{
                opacity: hasItems ? 1 : 0.5,
                cursor: hasItems ? 'pointer' : 'default',
              }}
            >
              <div className={styles.evidenceHeaderLeft}>
                <span className={styles.evidenceEmoji}>{EVIDENCE_CATEGORY_ICONS[key]}</span>
                <span className={styles.evidenceTitle}>{EVIDENCE_CATEGORY_LABELS[key]}</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {hasItems && <span className={styles.evidenceCount}>{items.length}</span>}
                {hasItems && (
                  <ChevronDown
                    className={`${styles.evidenceArrow} ${isOpen ? styles.evidenceArrowOpen : ''}`}
                    size={18}
                  />
                )}
              </div>
            </div>

            {isOpen && hasItems && (
              <div className={styles.evidenceContent}>
                {items.map(item => (
                  <EvidenceItemCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface EvidenceItemCardProps {
  item: EvidenceItem;
}

function EvidenceItemCard({ item }: EvidenceItemCardProps) {
  const getTitle = () => {
    if (item.title) return item.title;
    if (item.role && item.organization) return item.role;
    if (item.organization) return item.organization;
    return 'Evidence Entry';
  };

  const getSubtitle = () => {
    if (item.role && item.organization) return item.organization;
    return null;
  };

  return (
    <div className={styles.evidenceItem}>
      <div className={styles.evidenceItemHeader}>
        <div>
          <div className={styles.evidenceItemTitle}>{getTitle()}</div>
          {getSubtitle() && <div className={styles.evidenceItemOrg}>{getSubtitle()}</div>}
        </div>
        {item.date && <span className={styles.evidenceItemDate}>{item.date}</span>}
      </div>

      <p className={styles.evidenceItemDescription}>{item.description}</p>

      {item.url && (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.evidenceItemLink}
        >
          <ExternalLink size={13} />
          View Source
        </a>
      )}
    </div>
  );
}
